import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import dayjs from 'dayjs';
import {
  DayOff,
  DefaultSingleSelector,
  Holiday,
  WorkingTime,
} from 'src/components';
import { SubmitButton } from 'src/components/Button/SubmitButton';
import {
  typeOfHolidayArr,
  typeOfRuleArr,
  TypeOfRuleType,
} from 'src/components/SideBar/CalendarSideBar/components';
import { useAppDispatch } from 'src/hooks/redux';
import { CloseIcon, WorkingTimeSidebarInfoIcon } from 'src/Icons';
import { resetCurrentStudioData, setDaysOffToDelete, setDaysOffToPatch } from 'src/store/reducers/currentStudioSlice';
import { getDaysOffFilteredByReason } from 'src/utils/calendar';

import {
  DayOffRulesSection,
  HolidaySection,
  RulesSection,
  WeekDaySection,
} from './components';

interface PopupProps {
  isOpen: boolean;
  isEdit: boolean;
  onClose: () => void;
  onChangePeriodId?: (newId: any) => void;
  onSubmit: (params: any) => void;
  typeId: string;
  onChangeTypeId: (value: string) => void;
  workingTimes: Array<WorkingTime>;
  workingTimesIndex: number;
  daysOff: Array<DayOff>;
  daysOffReason: string;
  holidays: Array<Holiday>;
  typeOfHolidayId: string;
  period: any;
  onChangeTypeOfHolidayId: (value: string) => void;
  periodId?: any;
}

export const Popup = ({
  periodId,
  onChangePeriodId,
  isOpen,
  isEdit,
  onClose,
  onSubmit,
  typeId,
  onChangeTypeId,
  onChangeTypeOfHolidayId,
  workingTimes,
  workingTimesIndex,
  daysOff,
  daysOffReason,
  holidays,
  typeOfHolidayId,
  period,
}: PopupProps) => {
  const dispatch = useAppDispatch();

  const handleChangeSelectTypeOfRule = (value: string) => {
    onChangeTypeId(value);
  };

  const workTimesRef = useRef<any>();
  const workTimesRulesRef = useRef<any>();
  const daysOffRef = useRef<any>();
  const holidaysRef = useRef<any>();

  const handleSubmit = () => {
    const typeOfRule = typeOfRuleArr.find(({ id }) => id === typeId);

    if (typeOfRule?.type === TypeOfRuleType.WorkingTime) {
      // @ts-ignore
      const newDaysPeriods = workTimesRef?.current?.getDaysPeriods();
      // @ts-ignore
      const period = workTimesRulesRef?.current?.getPeriod();
      // @ts-ignore
      const periodId = workTimesRulesRef?.current?.getId();

      onChangePeriodId && onChangePeriodId(periodId);
      onSubmit({
        newDaysPeriods,
        period,
      });
    } else if (typeOfRule?.type === TypeOfRuleType.DaysOff) {
      // @ts-ignore
      const newDaysOff = daysOffRef?.current?.getDaysOff();
      dispatch(setDaysOffToDelete(deletedDaysOff))
      dispatch(setDaysOffToPatch(daysOffInputs))
      onSubmit({
        newDaysOff,
      });
    } else {
      // @ts-ignore
      const newHolidays = holidaysRef?.current?.getHolidays();

      onSubmit({
        newHolidays,
      });
    }
  };

  const [weekDaysState, setWeekDaysState] = useState<any>([]);
  const [holidaysState, setHolidaysState] = useState<any>([]);
  const [daysOffState, setDaysOffState] = useState<any>([]);
  const daysOffFilteredByReason = getDaysOffFilteredByReason(daysOff);

  const [deletedDaysOff, setDeletedDaysOff] = useState<any[]>([])

  const [daysOffInputs, setDaysOffInputs] = useState(
    daysOffFilteredByReason[daysOffReason]
      ? daysOffFilteredByReason[daysOffReason].map(
        (day: any, index: number) => ({
          id: day?.period?.from,
          label: `Day off date ${index + 1}`,
          value: day?.period?.from || "",
        })
      )
      : [
        {
          id: "1",
          label: "Day off date 1",
          value: "",
        },
      ]
  );
  useEffect(() => {
    if(daysOffReason) {
      setDaysOffInputs(
        daysOffFilteredByReason[daysOffReason]
          ? daysOffFilteredByReason[daysOffReason].map(
            (day: any, index: number) => ({
              id: day?.period?.from,
              label: `Day off date ${index + 1}`,
              value: day?.period?.from || "",
            })
          )
          : [
            {
              id: "1",
              label: "Day off date 1",
              value: "",
            },
          ]
      )
    }
  },[daysOffReason])

  const handleDayOffDeletion = (deletedValue: any) => {
    setDeletedDaysOff([...deletedDaysOff, deletedValue])
  };

  const handleWeekDaysChange = (newWeekDays: any) => {
    setWeekDaysState(newWeekDays);
  };
  const handleHolidaysChange = (newHolidays: any) => {
    setHolidaysState(newHolidays);
  };
  const handleDaysOffChange = (newDaysOff: any, newReason: string) => {
    setDaysOffState({ newDaysOff, newReason });
  };

  const isSubmitButtonValid = useMemo(() => {
    const isWorkingTimeFulfilled =
      weekDaysState &&
      weekDaysState.filter((row: { checked: boolean }) => row.checked).length >
        0 &&
      !weekDaysState.some(
        (row: { from: any; to: any }) =>
          row.from.includes('undefined') || row.to.includes('undefined')
      );

    const isHolidaysFulfilled =
      holidaysState &&
      holidaysState.filter((row: { checked: boolean }) => row.checked).length >
        0;

    function formatValue (value: any): string {
      if (typeof value === 'string') {
        const val = dayjs(value).format('YYYY-MM-DD');
        return val;
      } else if (dayjs.isDayjs(value)) {
        return value.format('YYYY-MM-DD');
      } else {
        throw new Error('Unsupported value type');
      }
    }

    function hasDuplicateValues (daysOffArray: any[]) {
      const valueSet = new Set();

      for (const dayOff of daysOffArray) {
        const formattedValue = formatValue(dayOff.value);

        if (valueSet.has(formattedValue)) {
          return true;
        }

        valueSet.add(formattedValue);
      }

      return false;
    }

    const isDaysOffFulfilled = daysOffInputs.every((input: any, index: number) =>
      input.label !== "" && input.value !== "" &&
      (index === 0 || (
        input.label !== daysOffInputs[index - 1].label ||
        formatValue(input.value) !== formatValue(daysOffInputs[index - 1].value)
      )) &&
      daysOffState.newReason !== '' &&
      !hasDuplicateValues(daysOffInputs)
    );

    // const isDaysOffFulfilled = daysOffInputs.every((input: any, index: number) =>
    //   input.label !== "" && input.value !== "" &&
    // (index === 0 || (input.label !== daysOffInputs[index - 1].label || input.value !== daysOffInputs[index - 1].value)) &&
    // daysOffState.newReason !== '' &&
    // !hasDuplicateValues(daysOffInputs)
    // );

    // function formatValue (value: any): string {
    //   if (typeof value === 'string') {
    //     return value; // Возвращаем строку как есть
    //   } else if (dayjs.isDayjs(value)) {
    //     return value.format(); // Форматируем объект dayjs в строку
    //   } else {
    //     throw new Error('Unsupported value type'); // Обрабатываем неподдерживаемый тип значения
    //   }
    // }

    // function hasDuplicateValues (daysOffArray: any[]) {
    //   const valueSet = new Set();

    //   for (const dayOff of daysOffArray) {
    //     const formattedValue = formatValue(dayOff.value);

    //     if (valueSet.has(formattedValue)) {
    //       return true;
    //     }

    //     valueSet.add(formattedValue);
    //   }

    //   return false;
    // }

    // function hasDuplicateValues (daysOffArray: any[]) {
    //   const valueSet = new Set();
    //   for (const dayOff of daysOffArray) {
    //     if (valueSet.has(dayOff.value.format())) {
    //       return true;
    //     }
    //     valueSet.add(dayOff.value.$D);
    //   }
    //   return false;
    // }

    const typeOfRule = typeOfRuleArr.find(({ id }) => id === typeId);

    if (typeOfRule?.type === TypeOfRuleType.WorkingTime) {
      return isWorkingTimeFulfilled;
    } else if (typeOfRule?.type === TypeOfRuleType.DaysOff) {
      return isDaysOffFulfilled;
    } else {
      return isHolidaysFulfilled;
    }
  }, [weekDaysState, holidaysState, daysOffState, typeId, daysOffInputs]);

  const renderPopupContent = useCallback(() => {
    const typeOfRule = typeOfRuleArr.find(({ id }) => id === typeId);

    if (typeOfRule?.type === TypeOfRuleType.WorkingTime) {
      return (
        <>
          <Box
            sx={{
              marginTop: '24px',
              marginBottom: '42px',
            }}
          >
            <DefaultSingleSelector
              value={typeId}
              valuesToSelect={typeOfRuleArr}
              onChanged={handleChangeSelectTypeOfRule}
              title="Type of rule"
            />
          </Box>
          <Box
            sx={{
              marginBottom: '24px',
            }}
          >
            <WeekDaySection
              ref={workTimesRef}
              onChange={handleWeekDaysChange}
              daysPeriods={workingTimes?.[workingTimesIndex]?.daysPeriods}
              label="Choose working days & Hours"
            />
          </Box>
          <Box
            sx={{
              marginBottom: '48px',
            }}
          >
            <RulesSection
              ref={workTimesRulesRef}
              period={workingTimes?.[workingTimesIndex]?.period}
              label="Set the rule"
              selectorId={periodId}
            />
          </Box>
        </>
      );
    } else if (typeOfRule?.type === TypeOfRuleType.DaysOff) {
      return (
        <>
          <Box
            sx={{
              marginTop: '24px',
              marginBottom: '42px',
            }}
          >
            <DefaultSingleSelector
              value={typeId}
              valuesToSelect={typeOfRuleArr}
              onChanged={handleChangeSelectTypeOfRule}
              title="Type of rule"
            />
          </Box>

          <Box
            sx={{
              marginBottom: '48px',
            }}
          >
            <DayOffRulesSection
              period={period}
              ref={daysOffRef}
              onChange={handleDaysOffChange}
              onDayDeletion={handleDayOffDeletion}
              daysOffReason={daysOffReason}
              daysOffInputs={daysOffInputs}
              setDaysOffInputs={setDaysOffInputs}
            />
          </Box>
        </>
      );
    } else {
      return (
        <>
          <Box
            sx={{
              marginTop: '24px',
              marginBottom: '42px',
            }}
          >
            <DefaultSingleSelector
              value={typeId}
              valuesToSelect={typeOfRuleArr}
              onChanged={handleChangeSelectTypeOfRule}
              title="Type of rule"
              leftContainerStyle={{
                borderBottomLeftRadius: '0px',
              }}
              rightContainerStyle={{
                borderBottomRightRadius: '0px',
              }}
            />
            <DefaultSingleSelector
              value={typeOfHolidayId}
              valuesToSelect={typeOfHolidayArr}
              onChanged={onChangeTypeOfHolidayId}
              title="Holiday worktime"
              leftContainerStyle={{
                borderTopLeftRadius: '0px',
                borderTop: 'none',
              }}
              rightContainerStyle={{
                borderTopRightRadius: '0px',
                borderTop: 'none',
              }}
            />
          </Box>

          <Box
            sx={{
              marginBottom: '48px',
            }}
          >
            <HolidaySection
              ref={holidaysRef}
              onChange={handleHolidaysChange}
              typeOfHolidayId={typeOfHolidayId}
              holidaysArr={holidays}
            />
          </Box>
        </>
      );
    }
  }, [
    typeId,
    handleChangeSelectTypeOfRule,
    workingTimes,
    workingTimesIndex,
    period,
    daysOffReason,
    daysOff,
    typeOfHolidayId,
    onChangeTypeOfHolidayId,
    holidays,
  ]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: '16px',
          width: '632px',
          maxWidth: '632px',
        },
      }}
    >
      <Box
        sx={{
          padding: '32px',
          borderRadius: '16px',
        }}
      >
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              color: '#B9B9B9',
              marginBottom: '12px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#080808',
                  marginRight: '8px',
                }}
              >
                {isEdit ? 'Update rule' : 'Create new rule'}
              </Typography>
              <WorkingTimeSidebarInfoIcon fontSize="inherit" />
            </Box>
            <Box
              onClick={onClose}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                fontSize: '24px',
                cursor: 'pointer',

                '&:hover': {
                  color: '#828282',
                },
              }}
            >
              <CloseIcon fontSize="inherit" />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            maxWidth: '330px',
          }}
        >
          <Typography
            sx={{
              color: '#9D9D9D',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Please select the type of rule you want to create, days and time and “repeat” settings.
          </Typography>
        </Box>

        {renderPopupContent()}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <SubmitButton
            variant="outlined"
            title="Cancel"
            onClick={onClose}
            sx={{
              width: '276px',
              borderRadius: '6px',
            }}
          />
          <SubmitButton
            title={isEdit ? 'Update rule' : 'Create rule'}
            disabled={!isSubmitButtonValid}
            onClick={handleSubmit}
            sx={{
              width: '276px',
              borderRadius: '6px',
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
